<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useWorkspaceStore } from "@/stores/workspace";
import { useRunsStore } from "@/stores/runs";
import { useUiStore } from "@/stores/ui";
import { ago, flowLabel } from "@/lib/format";
import { runTitle, runTone } from "@/lib/runState";
import StatusDot from "@/components/mc/StatusDot.vue";
import type { Tone } from "@/lib/runState";
import { runLink, ticketLink, ticketsFrom } from "@/lib/tickets";
import { listed, navigation } from "@/lib/navigation";
import { useFeedback } from "@/composables/useFeedback";
import { trapTab } from "@/lib/focusTrap";

interface Item {
  id: string;
  group: string;
  label: string;
  hint?: string;
  tone?: Tone;
  run: () => void;
}

const ui = useUiStore();
const runs = useRunsStore();
const auth = useAuthStore();
const workspace = useWorkspaceStore();
const router = useRouter();
const feedback = useFeedback();
const query = ref("");
const cursor = ref(0);
const input = ref<HTMLInputElement | null>(null);

const TICKET_RE = /^[A-Za-z][A-Za-z0-9_]{1,19}-\d+$/;

function go(to: string | { path: string; query?: Record<string, string> }) {
  ui.paletteOpen = false;
  void router.push(to);
}

const items = computed<Item[]>(() => {
  const q = query.value.trim();
  const ql = q.toLowerCase();
  const out: Item[] = [];
  if (TICKET_RE.test(q)) {
    const key = q.toUpperCase();
    if (workspace.acts) out.push({ id: "launch-ticket", group: "Ticket", label: `Start a run on ${key}`, hint: "↵", run: () => ui.openLaunch({ ticket: key }) });
    out.push({ id: "open-ticket", group: "Ticket", label: `Open ${key}`, hint: "tickets", run: () => go(ticketLink(key)) });
  }
  const actions: Item[] = navigation.flatMap((g) => g.items)
    .filter((n) => listed(n, auth.isAdmin, workspace.workspaces.some((w) => w.team_admin)))
    .map((n) => ({ id: n.to, group: "Go to", label: n.label, run: () => go(n.to) }));
  if (workspace.acts) actions.unshift({ id: "new", group: "Actions", label: "New run", hint: "N", run: () => ui.openLaunch() });
  // feedback about this page (2026-10-27): the palette closes first, so the screenshot shows the page, not the palette
  actions.unshift({ id: "feedback", group: "Actions", label: "Send feedback about this page", run: () => {
    const r = router.currentRoute.value;
    setTimeout(() => void feedback.start(String(r.meta.title ?? document.title), r.matched.at(-1)?.path ?? r.path), 150);
  } });
  out.push(...actions.filter((a) => !ql || a.label.toLowerCase().includes(ql)));

  const matches = runs.runs.filter((r) => {
    if (!ql) return true;
    return [runTitle(r), r.flow, flowLabel(r.flow), r.github_repo, r.triggered_by, r.job_id]
      .some((v) => (v ?? "").toLowerCase().includes(ql));
  });
  if (ql) {
    for (const t of ticketsFrom(runs.runs).filter((x) => x.key.toLowerCase().includes(ql)).slice(0, 5)) {
      out.push({ id: `t-${t.key}`, group: "Tickets", label: t.key, hint: `${t.runs.length} attempt${t.runs.length === 1 ? "" : "s"}`, tone: runTone(t.latest), run: () => go(ticketLink(t.key)) });
    }
  }
  for (const r of matches.slice(0, 8)) {
    out.push({
      id: `run-${r.job_id}`, group: "Runs", label: `${runTitle(r)} · ${flowLabel(r.flow)}`,
      hint: ago(r.created_at), tone: runTone(r), run: () => go(runLink(r)),
    });
  }
  return out;
});

const grouped = computed(() => {
  const groups: { name: string; items: (Item & { index: number })[] }[] = [];
  items.value.forEach((it, index) => {
    let g = groups.find((x) => x.name === it.group);
    if (!g) groups.push((g = { name: it.group, items: [] }));
    g.items.push({ ...it, index });
  });
  return groups;
});

watch(() => ui.paletteOpen, async (open) => {
  if (!open) return;
  query.value = "";
  cursor.value = 0;
  await nextTick();
  input.value?.focus();
});
watch(query, () => { cursor.value = 0; });

const paletteEl = ref<HTMLElement | null>(null);
function onKey(e: KeyboardEvent) {
  if (e.key === "ArrowDown") { e.preventDefault(); cursor.value = Math.min(cursor.value + 1, items.value.length - 1); }
  else if (e.key === "ArrowUp") { e.preventDefault(); cursor.value = Math.max(cursor.value - 1, 0); }
  else if (e.key === "Enter") { e.preventDefault(); items.value[cursor.value]?.run(); }
  else if (e.key === "Escape") { ui.paletteOpen = false; }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="pal">
      <div v-if="ui.paletteOpen" class="scrim" @mousedown.self="ui.paletteOpen = false">
        <div ref="paletteEl" class="palette" role="dialog" aria-modal="true" aria-label="Command palette" @keydown.tab="(e: KeyboardEvent) => trapTab(e, paletteEl)">
          <div class="input-row">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input ref="input" v-model="query" placeholder="Jump to a run, paste a Jira key, or type a command…" @keydown="onKey" />
            <kbd>esc</kbd>
          </div>
          <div class="list">
            <template v-for="g in grouped" :key="g.name">
              <div class="group">{{ g.name }}</div>
              <button
                v-for="it in g.items" :key="it.id" class="item" :data-on="it.index === cursor"
                @mouseenter="cursor = it.index" @click="it.run()"
              >
                <StatusDot v-if="it.tone" :tone="it.tone" />
                <span v-else class="glyph">›</span>
                <span class="label">{{ it.label }}</span>
                <span v-if="it.hint" class="hint">{{ it.hint }}</span>
              </button>
            </template>
            <p v-if="!items.length" class="none">Nothing matches “{{ query }}”.</p>
          </div>
          <div class="foot"><span><kbd>↑</kbd><kbd>↓</kbd> move</span><span><kbd>↵</kbd> open</span><span><kbd>⌘K</kbd> toggle</span></div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.scrim { position: fixed; inset: 0; z-index: 60; background: #0008; backdrop-filter: blur(3px); display: flex; justify-content: center; align-items: flex-start; padding-top: 12vh; }
.palette { width: min(640px, 92vw); max-height: 64vh; display: flex; flex-direction: column; background: var(--bg-elev); border: 1px solid var(--line); border-radius: var(--radius-m); box-shadow: var(--shadow-3); overflow: hidden; }
.input-row { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-4) var(--space-5); border-bottom: 1px solid var(--line-2); color: var(--muted); }
.input-row input { flex: 1; border: 0; outline: none; background: transparent; font: inherit; font-size: var(--text-md); color: var(--ink); }
.list { overflow-y: auto; padding: var(--space-2); }
.group { padding: var(--space-3) var(--space-3) var(--space-2); font-family: var(--font-ui); font-size: var(--text-xs); letter-spacing: 0; text-transform: none; color: var(--faint); }
.item { display: flex; align-items: center; gap: var(--space-3); width: 100%; height: 38px; padding: 0 var(--space-3); border: 0; border-radius: var(--radius-s); background: transparent; font: inherit; font-size: var(--text-sm); color: var(--ink); cursor: pointer; text-align: left; }
.item[data-on="true"] { background: var(--brand-soft); color: var(--brand); }
.glyph { width: 8px; color: var(--faint); }
.label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hint { font-size: var(--text-xs); color: var(--faint); }
.none { padding: var(--space-5); color: var(--muted); font-size: var(--text-sm); margin: 0; }
.foot { display: flex; gap: var(--space-5); padding: var(--space-3) var(--space-4); border-top: 1px solid var(--line-2); font-size: var(--text-xs); color: var(--faint); }
.foot span { display: inline-flex; align-items: center; gap: var(--space-2); }
.pal-enter-active, .pal-leave-active { transition: opacity var(--speed-quick) var(--ease); }
.pal-enter-active .palette, .pal-leave-active .palette { transition: transform var(--speed-med) var(--ease-out), opacity var(--speed-med) var(--ease-out); }
.pal-enter-from, .pal-leave-to { opacity: 0; }
.pal-enter-from .palette, .pal-leave-to .palette { transform: translateY(-8px) scale(.98); opacity: 0; }
</style>
